// ExplainBlockPanel — Popover/sheet som visar AI-genererad förklaring av
// ett block. Använder useExplainBlock-hooken för fetch + state. Öppnas
// från BlockContextMenu ("Förklara block…") eller höger-klick.
//
// Flow: dialog öppnas i CONFIRM-läge med mode-val + cost-estimate.
// Ingen auto-fetch — user måste explicit klicka "Förklara" för att
// debit:a credits. Efter resultat kan user byta mode (= ny confirm).

import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Loader2, AlertCircle, X, RotateCcw, Coins } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useExplainBlock, type ExplainMode } from '@/hooks/useExplainBlock';
import { estimateAiCost, totalCreditsOf } from '@/lib/creditCostEstimator';
import type { Connector, WorkflowBlock } from '@/types/workflow';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  block: WorkflowBlock | null;
  connectors: Connector[];
  sheetBlocks: WorkflowBlock[];
  workflowName?: string;
  lang?: 'sv' | 'en';
}

// Token-budgets matchar edge-fn:ns max_tokens-cap per mode + ~150 tokens
// prompt-overhead (system + block-context). Ger user en realistisk
// estimat innan de bekräftar.
const MODE_TOKENS: Record<ExplainMode, { prompt: number; output: number; label: string }> = {
  brief: { prompt: 150, output: 220, label: 'Kort (~2-3 meningar)' },
  detailed: { prompt: 150, output: 500, label: 'Detaljerad (~5-7 meningar, data-flow)' },
};

// Modellen edge-fn:n ringer (gemini-3-flash-preview). Inte i credit-rates-
// tabellen → faller på defaultTokensPerCredit = 2000. Hardcoded eftersom
// edge-fn-modell-bytet kräver explicit klient-uppdatering ändå.
const EXPLAIN_MODEL = 'google/gemini-3-flash-preview';

function estimateModeCredits(mode: ExplainMode): number {
  const { prompt, output } = MODE_TOKENS[mode];
  const estimate = estimateAiCost({
    promptTokens: prompt,
    completionTokens: output,
    model: EXPLAIN_MODEL,
  });
  return totalCreditsOf(estimate);
}

export function ExplainBlockPanel({
  open, onOpenChange, block, connectors, sheetBlocks, workflowName, lang = 'sv',
}: Props) {
  const [mode, setMode] = useState<ExplainMode>('brief');
  // hasConfirmed=false → visa confirm-vy. true → visa resultat-vy.
  // Resetas när dialogen stängs eller block:et byts.
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const { explanation, isLoading, error, correlationId, fetch, reset } = useExplainBlock({
    block, connectors, sheetBlocks, workflowName,
  });

  // Reset state när dialogen stängs eller block byts.
  useEffect(() => {
    if (!open) {
      reset();
      setMode('brief');
      setHasConfirmed(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, block?.id]);

  const blockName = block?.config.name ?? 'Block';

  const briefCost = useMemo(() => estimateModeCredits('brief'), []);
  const detailedCost = useMemo(() => estimateModeCredits('detailed'), []);
  const selectedCost = mode === 'brief' ? briefCost : detailedCost;

  const handleConfirm = () => {
    setHasConfirmed(true);
    void fetch(lang, mode);
  };

  const handleSwitchMode = (next: ExplainMode) => {
    if (next === mode) return;
    setMode(next);
    setHasConfirmed(false); // tvinga ny confirm vid mode-byte
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="explain-block-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Förklara block: {blockName}
          </DialogTitle>
          <DialogDescription>
            AI-genererad sammanfattning. Kostar credits — välj mode och bekräfta nedan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Mode-val (alltid synligt) */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Välj förklarings-mode</p>
            <div className="grid grid-cols-2 gap-2" data-testid="explain-mode-toggle">
              <button
                type="button"
                onClick={() => handleSwitchMode('brief')}
                disabled={isLoading}
                className={`p-3 rounded-md border text-left transition-colors ${
                  mode === 'brief'
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                    : 'border-border hover:bg-muted/50'
                }`}
                data-testid="explain-mode-brief"
              >
                <div className="text-xs font-semibold">{MODE_TOKENS.brief.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                  <Coins className="w-3 h-3" />
                  ≈ {briefCost.toFixed(2)} credits
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleSwitchMode('detailed')}
                disabled={isLoading}
                className={`p-3 rounded-md border text-left transition-colors ${
                  mode === 'detailed'
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                    : 'border-border hover:bg-muted/50'
                }`}
                data-testid="explain-mode-detailed"
              >
                <div className="text-xs font-semibold">{MODE_TOKENS.detailed.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                  <Coins className="w-3 h-3" />
                  ≈ {detailedCost.toFixed(2)} credits
                </div>
              </button>
            </div>
          </div>

          {/* Confirm-vy: visas innan första fetch */}
          {!hasConfirmed && !isLoading && !explanation && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-100">
              <strong>Bekräfta:</strong> Detta drar ≈ {selectedCost.toFixed(2)} credits från
              ditt saldo (faktisk kostnad varierar med token-användning).
            </div>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>AI förklarar detta block…</span>
            </div>
          )}

          {!isLoading && error === 'edge-function-not-deployed' && (
            <div className="flex items-start gap-2 text-muted-foreground">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                AI-funktionen är inte aktiverad ännu. Kontakta support eller
                vänta tills admin har deploy:at edge-funktionen.
              </span>
            </div>
          )}

          {!isLoading && error && error !== 'edge-function-not-deployed' && (
            <div className="flex items-start gap-2 text-destructive">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Kunde inte hämta förklaring: {error}
                {correlationId && (
                  <span className="block text-[10px] font-mono opacity-70 mt-0.5">
                    Felkod: {correlationId}
                  </span>
                )}
              </span>
            </div>
          )}

          {!isLoading && explanation && (
            <div className="leading-relaxed whitespace-pre-wrap" data-testid="explain-result">
              {explanation}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            {!hasConfirmed && !explanation && (
              <Button
                size="sm"
                onClick={handleConfirm}
                disabled={isLoading || !block}
                data-testid="explain-confirm"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                Förklara (≈ {selectedCost.toFixed(2)} credits)
              </Button>
            )}
            {error && error !== 'edge-function-not-deployed' && (
              <Button size="sm" variant="outline" onClick={() => void fetch(lang, mode)}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Försök igen
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
              <X className="w-3.5 h-3.5 mr-1" /> Stäng
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
